import { useLocation } from "@reach/router"
import get from "lodash/get"
import qs from "qs"
import React, { useCallback, useEffect, useState } from "react"
import { getQueryStringValue } from "../util"
import ThemeContext from "../context/ThemeContext"
import BodyStyles from "./BodyStyles"
import { ButtonLink } from "./Button"
import Modal from "./Modal"
import RichText from "./RichText"

const ContentModal = ({
  contentItem,
  currentLanguage,
  link,
  maxContainerWidth = 1024,
  theme,
  colorScheme,
  iconColorScheme,
  textSize,
}) => {
  const [showModal, setShowModal] = useState(false)
  const alignColumns = get(
    contentItem,
    "elements.align_columns.value[0].codename"
  )
  const asidePosition = get(
    contentItem,
    "elements.aside_position.value[0].codename"
  )
  const asideWidth =
    (get(contentItem, "elements.aside_width.value[0].codename") || "n40") ===
    "n40"
      ? "2/5"
      : "1/2"
  const contentCropWidth = {
    "2/5": maxContainerWidth * 0.6,
    "1/2": maxContainerWidth * 0.5,
  }[asideWidth]
  const asideCropWidth = {
    "2/5": maxContainerWidth * 0.4,
    "1/2": maxContainerWidth * 0.5,
  }[asideWidth]


  const { hydrated: isClient } = React.useContext(ThemeContext)
  const itemParam = getQueryStringValue("item")
  const location = useLocation()
  const title = get(contentItem, "elements.title.value")
  const urlSearchParams = qs.parse(location.search, { ignoreQueryPrefix: true })
  const urlSlug = get(contentItem, "elements.url_slug.value")

  const handleUrlChange = useCallback(() => {
    if (itemParam === urlSlug && !showModal) {
      setShowModal(true)
    } else if (itemParam !== urlSlug && showModal) {
      setShowModal(false)
    }
  }, [itemParam, showModal, urlSlug])

  const handleModalChange = (show) => {
    // Updates the URL search params with the modal that is open (and removes it when closed)
    if (show) {
      urlSearchParams.item = urlSlug
      window.history.pushState(
        {},
        ``,
        `${location.pathname}?${qs.stringify(urlSearchParams)}`
      )
    } else {
      window.history.pushState({}, ``, location.pathname)
    }

    setShowModal(show)
  }

  // Allows the modal to open when page initially loads
  handleUrlChange()

  useEffect(() => {
    if (!isClient) {
      return
    }

    const popStateListener = (event) => {
      handleUrlChange()
    }

    // Updates visibility of the modal when the URL changes
    window.addEventListener("popstate", popStateListener)

    return function cleanup() {
      window.removeEventListener("popstate", popStateListener)
    }
  }, [handleUrlChange, isClient])

  return (
    <>
      <ButtonLink
        iconColorScheme={iconColorScheme}
        onClick={(event) => {
          handleModalChange(true)
          event.preventDefault()
        }}
        {...{
          "data-analytics-title": get(contentItem, "system.name"),
          "data-analytics-category": "Modal",
        }}
      >
        {link.label}
        {link.style === "more" && <i className="icon-chevron-right"></i>}
      </ButtonLink>
      <Modal
        isOpen={showModal === true}
        onChange={handleModalChange}
        modalSize={`lg`}
        ariaLabel={title}
      >
        <div className={`flex flex-wrap text-left -mt-8 -ml-8`}>
          <div
            className={`flex w-full ${
              asideWidth === `2/5` ? `md:w-1/2 lg:w-3/5` : `md:w-1/2`
            } ${alignColumns === `center` ? `items-center` : ``} pl-8 mt-8`}
          >
            <BodyStyles
              className={`w-full`}
              theme={theme}
              colorScheme={colorScheme}
            >
              <RichText
                colorScheme={colorScheme}
                content={get(contentItem, "elements.text.value")}
                currentLanguage={currentLanguage}
                linkedItems={get(contentItem, "elements.text.modular_content")}
                links={get(contentItem, "elements.text.links")}
                maxContainerWidth={contentCropWidth}
                textSize={textSize}
                theme={theme}
                region={"modal"}
              />
            </BodyStyles>
          </div>
          <div
            className={`flex w-full mt-8 ${
              asideWidth === `2/5` ? `md:w-1/2 lg:w-2/5` : `md:w-1/2`
            } ${asidePosition === `prefer_left` ? `md:order-first` : ``} ${
              alignColumns === `center` ? `items-center` : ``
            } pl-8 mt-8`}
          >
            <BodyStyles
              className={`w-full`}
              theme={theme}
              colorScheme={colorScheme}
            >
              <RichText
                colorScheme={colorScheme}
                content={get(contentItem, "elements.aside.value")}
                currentLanguage={currentLanguage}
                linkedItems={get(contentItem, "elements.aside.modular_content")}
                links={get(contentItem, "elements.aside.links")}
                maxContainerWidth={asideCropWidth}
                textSize={textSize}
                theme={theme}
                region={"modal"}
                spacing="sm"
              />
            </BodyStyles>
          </div>
        </div>
      </Modal>
    </>
  )
}

export default ContentModal
